import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useCMS } from '../../context/CMSContext';
import { toast, Toaster } from 'sonner';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, Save, Calendar, Tag, BookOpen, ExternalLink, Image as ImageIcon } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import ImagePicker from '../../components/ui/ImagePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminCaseStudiesPage = () => {
  const { getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost } = useCMS();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '', slug: '', excerpt: '', content: '', 
    featured_image: '', category: 'Success Story', tags: [], author: 'GetCare Team'
  });
  const [newTag, setNewTag] = useState('');
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    try {
      const data = await getBlogPosts(false);
      setPosts(data);
    } catch (e) {
      toast.error('Failed to load case studies');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await updateBlogPost(editingId, formData);
        toast.success('Case study updated');
      } else {
        await createBlogPost(formData);
        toast.success('Case study created');
      }
      resetForm();
      loadPosts();
    } catch (e) {
      toast.error(e.message || 'Failed to save case study');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study?')) return;
    try {
      await deleteBlogPost(id);
      toast.success('Case study deleted');
      loadPosts();
    } catch (e) {
      toast.error('Failed to delete case study');
    }
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featured_image: post.featured_image || '',
      category: post.category || 'Success Story',
      tags: post.tags || [],
      author: post.author
    });
    setEditingId(post.id);
    setShowForm(true);
  };

  const handleTogglePublish = async (post) => {
    try {
      await updateBlogPost(post.id, { is_published: !post.is_published });
      toast.success(`Case study ${post.is_published ? 'unpublished' : 'published'}`);
      loadPosts();
    } catch (e) {
      toast.error('Failed to update case study');
    }
  };

  const handlePreview = (post) => {
    // Open preview in new tab
    window.open(`/case-studies/${post.slug}?preview=true`, '_blank');
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const resetForm = () => {
    setFormData({
      title: '', slug: '', excerpt: '', content: '',
      featured_image: '', category: 'Success Story', tags: [], author: 'GetCare Team'
    });
    setEditingId(null);
    setShowForm(false);
  };

  const categories = ['Success Story', 'NDIS Provider', 'SIL Provider', 'Support Coordinator', 'Allied Health', 'Plan Manager'];

  if (loading) return <div className="text-text-secondary">Loading...</div>;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />
      
      {/* Image Picker Modal */}
      <ImagePicker
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={(url) => setFormData({ ...formData, featured_image: url })}
        currentImage={formData.featured_image}
      />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Case Studies</h1>
          <p className="text-text-secondary mt-1">Share success stories from NDIS providers</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-tech-blue hover:bg-tech-blue/90">
          <Plus size={18} className="mr-2" />
          New Case Study
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-5xl border-border-line my-8">
            <CardHeader className="flex flex-row items-center justify-between pb-3 sticky top-0 bg-surface z-10 border-b border-border-line">
              <div className="flex items-center space-x-3">
                <BookOpen className="text-tech-blue" size={24} />
                <CardTitle>{editingId ? 'Edit' : 'New'} Case Study</CardTitle>
              </div>
              <div className="flex items-center space-x-2">
                {editingId && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`/case-studies/${formData.slug}?preview=true`, '_blank')}
                    className="text-care-teal"
                  >
                    <Eye size={16} className="mr-1" />
                    Preview
                  </Button>
                )}
                <button onClick={resetForm} className="text-text-secondary hover:text-text-primary">
                  <X size={20} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Main Content - 2 columns */}
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Title *</label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={handleTitleChange}
                        className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary text-lg font-medium"
                        placeholder="e.g., How CareFirst Saved 60% Admin Time..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Summary *</label>
                      <textarea
                        required
                        rows={2}
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-border-line bg-surface text-text-primary resize-none"
                        placeholder="Brief description of the case study..."
                      />
                    </div>

                    <div data-color-mode="light">
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Content * <span className="text-xs text-tech-blue">(Markdown supported)</span>
                      </label>
                      <MDEditor
                        value={formData.content}
                        onChange={(val) => setFormData({ ...formData, content: val || '' })}
                        height={400}
                        preview="edit"
                        className="!border-border-line"
                      />
                      <div className="mt-2 p-3 bg-page-bg rounded-lg text-xs text-text-secondary">
                        <strong>Tips:</strong> Use ## for sections, > for quotes, - for bullet points
                        <br />
                        <strong>Suggested structure:</strong> The Challenge → The Solution → The Results → Key Takeaways
                      </div>
                    </div>
                  </div>

                  {/* Sidebar - 1 column */}
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">URL Slug *</label>
                      <input
                        type="text"
                        required
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary font-mono text-sm"
                        placeholder="case-study-url"
                      />
                      <p className="text-xs text-text-secondary mt-1">/case-studies/{formData.slug || 'your-slug'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Category *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Provider/Company</label>
                      <input
                        type="text"
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        className="w-full px-4 py-2 rounded-lg border border-border-line bg-surface text-text-primary"
                        placeholder="e.g., CareFirst NDIS"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Featured Image</label>
                      <div className="space-y-2">
                        {formData.featured_image ? (
                          <div className="relative">
                            <img 
                              src={formData.featured_image} 
                              alt="Featured" 
                              className="w-full h-32 object-cover rounded-lg border border-border-line" 
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, featured_image: '' })}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="h-32 border-2 border-dashed border-border-line rounded-lg flex items-center justify-center bg-page-bg">
                            <ImageIcon className="text-text-secondary" size={32} />
                          </div>
                        )}
                        <div className="flex space-x-2">
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowImagePicker(true)}
                            className="flex-1"
                          >
                            <ImageIcon size={16} className="mr-2" />
                            {formData.featured_image ? 'Change' : 'Select'} Image
                          </Button>
                        </div>
                        <input
                          type="text"
                          value={formData.featured_image}
                          onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                          className="w-full px-3 py-1.5 rounded-lg border border-border-line bg-surface text-text-primary text-xs"
                          placeholder="Or paste URL directly..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">Tags</label>
                      <div className="flex space-x-2 mb-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                          className="flex-1 px-3 py-2 rounded-lg border border-border-line bg-surface text-text-primary text-sm"
                          placeholder="Add tag..."
                        />
                        <Button type="button" onClick={addTag} variant="outline" size="sm">
                          <Plus size={16} />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag) => (
                          <span key={tag} className="inline-flex items-center px-2 py-1 rounded-full bg-tech-blue/10 text-tech-blue text-xs">
                            <Tag size={10} className="mr-1" />
                            {tag}
                            <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-500">
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Preview Summary */}
                    <div className="p-4 bg-care-teal/10 rounded-lg border border-care-teal/20">
                      <h4 className="text-sm font-semibold text-text-primary mb-2 flex items-center">
                        <Eye size={14} className="mr-2 text-care-teal" />
                        Preview Summary
                      </h4>
                      <div className="space-y-1 text-xs text-text-secondary">
                        <p><strong>Title:</strong> {formData.title || 'Untitled'}</p>
                        <p><strong>Category:</strong> {formData.category}</p>
                        <p><strong>Provider:</strong> {formData.author}</p>
                        <p><strong>Tags:</strong> {formData.tags.join(', ') || 'None'}</p>
                        <p><strong>Content:</strong> {formData.content?.length || 0} characters</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-6 border-t border-border-line">
                  <div>
                    {editingId && (
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => window.open(`/case-studies/${formData.slug}?preview=true`, '_blank')}
                      >
                        <ExternalLink size={16} className="mr-2" />
                        Open Preview
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                    <Button type="submit" className="bg-tech-blue hover:bg-tech-blue/90">
                      <Save size={18} className="mr-2" />
                      {editingId ? 'Update' : 'Create'} Case Study
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Posts List */}
      <div className="space-y-4">
        {posts.map((post) => (
          <Card key={post.id} className={`border-border-line ${!post.is_published ? 'border-l-4 border-l-yellow-400' : 'border-l-4 border-l-care-teal'}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`text-xs px-2 py-1 rounded ${post.is_published ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                      {post.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-tech-blue/10 text-tech-blue">
                      {post.category || 'Case Study'}
                    </span>
                    {post.views > 0 && (
                      <span className="text-xs text-text-secondary flex items-center">
                        <Eye size={12} className="mr-1" />
                        {post.views} views
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-text-primary mb-1">{post.title}</h3>
                  <p className="text-sm text-text-secondary line-clamp-2 mb-2">{post.excerpt}</p>
                  <div className="flex items-center text-xs text-text-secondary space-x-4">
                    <span className="flex items-center">
                      <Calendar size={12} className="mr-1" />
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                    <span>{post.author}</span>
                    <span className="text-care-teal">/case-studies/{post.slug}</span>
                  </div>
                </div>
                {post.featured_image && (
                  <img src={post.featured_image} alt="" className="w-24 h-24 object-cover rounded-lg ml-4" />
                )}
              </div>
              <div className="flex items-center justify-end mt-4 pt-3 border-t border-border-line space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePreview(post)}
                  className="text-xs text-care-teal"
                >
                  <ExternalLink size={14} className="mr-1" />
                  Preview
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleTogglePublish(post)}
                  className="text-xs"
                >
                  {post.is_published ? <EyeOff size={14} className="mr-1" /> : <Eye size={14} className="mr-1" />}
                  {post.is_published ? 'Unpublish' : 'Publish'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleEdit(post)} className="text-xs">
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(post.id)} className="text-xs text-red-500 hover:text-red-600">
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12 text-text-secondary">
          <BookOpen className="mx-auto mb-4 text-text-secondary" size={48} />
          <p className="mb-2">No case studies yet.</p>
          <p className="text-sm">Click "New Case Study" to share your first success story.</p>
        </div>
      )}
    </div>
  );
};

export default AdminCaseStudiesPage;
